// M7.3 — React hook that calls M7.2's `/api/agent/surface-context`.
//
// Contract (amendment #130 / backend #743 — re-check after #743's fix lands):
//   POST /api/agent/surface-context
//   body: { surface, brandId, planId?, focusId? }
//   200:  { summary, nudges[], starters[] } plus optional fields. See types.ts.
//
// The client does not send a narration arm. The server chooses rules
// vs LLM text ($0.50/operator/day inside the $5/day agent cap). A
// non-OK response that still carries a summary keeps that summary so
// a narration failure does not blank the rules text.
//
// Fetch gate: VITE_AGENT_SURFACE_CONTEXT_ENABLED === 'true' AND the
// current pathname matches isAgentV2Route. `/catalog` never fetches.
// Marks and nudges have their own flags and are applied by the surface.
//
// brandId in the body is the same id apiFetch puts on X-Brand-Id
// (localStorage.brand_id). An agent-context override that disagrees
// with that header skips the fetch. Advertiser and operator are part
// of the query key so a workspace or operator change cannot reuse the
// previous payload. When the response includes brandId and it disagrees
// with the request, the payload is dropped.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../../auth/apiFetch';
import { useAuth } from '../../auth/AuthContext';
import { useBrand } from '../../brand/BrandContext';
import { isAgentV2Route } from '../../lib/featureFlags';
import { surfaceContextEnabled } from './flags';
import type {
  AttentionTarget,
  NarrationStatus,
  Nudge,
  Starter,
  SurfaceContextInput,
  SurfaceContextResponse
} from './types';

const ENDPOINT = '/api/agent/surface-context';

export type UseSurfaceContextResult = {
  data:      SurfaceContextResponse | null;
  isLoading: boolean;
  error:     string | null;
  queryKey:  readonly unknown[];
};

function readStorage(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

function asNarrationStatus(value: unknown): NarrationStatus | undefined {
  if (value === 'ok' || value === 'unavailable' || value === 'failed' || value === 'rules') return value;
  return undefined;
}

export function parseSurfaceContext(json: unknown): SurfaceContextResponse | null {
  if (!json || typeof json !== 'object') return null;
  const raw = json as Record<string, unknown>;
  const summary = typeof raw.summary === 'string' ? raw.summary : null;
  const rulesText = typeof raw.rulesText === 'string' ? raw.rulesText : undefined;
  if (summary == null && rulesText == null) return null;
  const out: SurfaceContextResponse = { summary: summary ?? rulesText as string };
  if (rulesText !== undefined) out.rulesText = rulesText;
  if (Array.isArray(raw.nudges)) out.nudges = raw.nudges as Nudge[];
  if (Array.isArray(raw.starters)) out.starters = raw.starters as Starter[];
  if (Array.isArray(raw.attentionTargets)) out.attentionTargets = raw.attentionTargets as AttentionTarget[];
  if (typeof raw.cacheKey === 'string') out.cacheKey = raw.cacheKey;
  if (typeof raw.lastGeneratedAt === 'string') out.lastGeneratedAt = raw.lastGeneratedAt;
  if (typeof raw.brandId === 'string') out.brandId = raw.brandId;
  if (raw.narrationArm === 'rules' || raw.narrationArm === 'llm') out.narrationArm = raw.narrationArm;
  if (typeof raw.narration === 'string') out.narration = raw.narration;
  const status = asNarrationStatus(raw.narrationStatus);
  if (status) out.narrationStatus = status;
  return out;
}

export function useSurfaceContext(input: SurfaceContextInput): UseSurfaceContextResult {
  const fetchOn = surfaceContextEnabled();
  const { pathname } = useLocation();
  const onV2 = isAgentV2Route(pathname);
  const brand = useBrand();
  const auth = useAuth();

  const uiBrandId = brand.activeBrand?.id ?? null;
  const headerBrandId = readStorage('brand_id');
  // Body brand must equal the X-Brand-Id apiFetch will send.
  const brandId = uiBrandId && headerBrandId && uiBrandId === headerBrandId ? uiBrandId : null;
  const headerAdvertiserId = readStorage('advertiser_id');
  const uiAdvertiserId = brand.activeAdvertiserId ?? null;
  const operatorId = auth.status === 'authenticated' && auth.user.userId
    ? auth.user.userId
    : null;
  const enabled = fetchOn && onV2 && Boolean(brandId) && Boolean(operatorId);

  const queryKey = useMemo(
    () => [
      'agent-surface-context',
      input.surface,
      brandId,
      headerAdvertiserId,
      uiAdvertiserId,
      operatorId,
      input.planId ?? null,
      input.focusId ?? null
    ] as const,
    [input.surface, brandId, headerAdvertiserId, uiAdvertiserId, operatorId, input.planId, input.focusId]
  );

  const scope = `${enabled ? 1 : 0}\0${queryKey.join('\0')}`;
  const [data, setData] = useState<SurfaceContextResponse | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scopeSeen, setScopeSeen] = useState(scope);
  // Drop the previous brand's payload in the same render that the
  // scope changes, so it cannot paint on the next brand.
  if (scopeSeen !== scope) {
    setScopeSeen(scope);
    setData(null);
    setError(null);
    setLoading(enabled);
  }

  const brandRef = useRef(brandId);
  brandRef.current = brandId;

  useEffect(() => {
    if (!enabled || !brandId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const requestedBrandId = brandId;
    const controller = new AbortController();
    let cancelled = false;
    const stale = () => cancelled || controller.signal.aborted || brandRef.current !== requestedBrandId;

    setLoading(true);
    setError(null);

    const body: Record<string, string> = {
      surface: input.surface,
      brandId: requestedBrandId
    };
    if (input.planId) body.planId = input.planId;
    if (input.focusId) body.focusId = input.focusId;

    apiFetch(ENDPOINT, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
      signal:  controller.signal
    })
      .then(async (res) => {
        if (stale()) return;
        let json: unknown = null;
        try { json = await res.json(); } catch { json = null; }
        if (stale()) return;
        const parsed = parseSurfaceContext(json);
        if (parsed?.brandId && parsed.brandId !== requestedBrandId) {
          setData(null);
          setError(null);
          setLoading(false);
          return;
        }
        if (!res.ok) {
          if (stale()) return;
          if (parsed) {
            // Any non-OK response is a narration/transport failure.
            // Keep the rules summary; do not surface LLM prose from it.
            setData({ ...parsed, narrationStatus: 'unavailable' });
            setError(`surface-context ${res.status}`);
          } else {
            setData(null);
            setError(`surface-context ${res.status}`);
          }
          setLoading(false);
          return;
        }
        if (!parsed) {
          setData(null);
          setError('surface-context invalid');
          setLoading(false);
          return;
        }
        setData(parsed);
        setError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (stale() || isAbortError(err)) return;
        setData(null);
        setError(err instanceof Error ? err.message : 'surface-context failed');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, brandId, headerAdvertiserId, uiAdvertiserId, operatorId, input.surface, input.planId, input.focusId]);

  return { data, isLoading, error, queryKey };
}

export type AttentionSelection = {
  ids: Set<string>;
  reasonFor: (id: string) => string | undefined;
};

const NO_REASON: AttentionSelection['reasonFor'] = () => undefined;

/** Pure selector. Does not fetch — pair it with one useSurfaceContext call. */
export function selectAttentionTargets(
  data: SurfaceContextResponse | null | undefined,
  entityType: string
): AttentionSelection {
  const ids = new Set<string>();
  const reasons = new Map<string, string>();
  const targets = data?.attentionTargets;
  if (!Array.isArray(targets)) return { ids, reasonFor: NO_REASON };
  for (const target of targets) {
    if (!target || typeof target !== 'object') continue;
    if (target.entityType !== entityType) continue;
    if (!target.entityId || typeof target.entityId !== 'string') continue;
    ids.add(target.entityId);
    if (target.reason && typeof target.reason === 'string') reasons.set(target.entityId, target.reason);
  }
  return { ids, reasonFor: (id: string) => reasons.get(id) };
}

// Own fetch. Do not also call useSurfaceContext for the same input —
// that would POST twice. Surfaces that need both the summary and the
// id set should call useSurfaceContext once and selectAttentionTargets.
export function useAttentionTargets(
  input: SurfaceContextInput,
  entityType: string
): AttentionSelection {
  const { data } = useSurfaceContext(input);
  return useMemo(() => selectAttentionTargets(data, entityType), [data, entityType]);
}
